import { Alert, Button, Icon, Spinner } from '@acpaas-ui/react-components';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fetchReset } from '../../actions/fetch.actions';
import { resourceReset } from '../../actions/resources.actions';
import { fetchForTable } from '../../actions/table.actions';
import FileUpload from '../../components/core/FileUpload';
import OfferingDetailView from '../../components/offerings/OfferingDetailView';
import OfferingSchoolSelect from '../../components/offerings/OfferingSchoolSelect';
import { OFFERING_DETAILS_BREADCRUMBS } from '../../constants/breadcrumbs.constants';
import { DOSSIERS_PATH, LOGIN_PATH } from '../../constants/paths.constants';
import { DOSSIER_TYPES, FORM_TYPES, FORM_TYPE_PROP } from '../../constants/settings.constants';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs.hook';
import { useSingleResource } from '../../hooks/useSingleResource.hook';
import { getSubmittedByFromUser } from '../../lib/form.utils';
import { FORMS_ENGINE_FORM_RESOURCE } from '../../resources/forms.engine.form.resource';
import { OFFERINGS_RESOURCE } from '../../resources/offerings.resource';
import { SCHOOLS_RESOURCE } from '../../resources/schools.resource';
import { authenticatedSelector, currentUserSelector } from '../../selectors/auth.selectors';
import { fetchErrorSelector, fetchingSelector, shouldFetchSelector } from '../../selectors/fetch.selectors';
import { tableDataSelector } from '../../selectors/resources.selectors';
import FormsEngineWidget from '../forms-engine/FormsEngineWidget';
import dossiersService from '../../services/api/dossiers.service';

const resource = SCHOOLS_RESOURCE;

const OfferingDetails = () => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const shouldFetchSchools = useSelector((state) => shouldFetchSelector(state, { resource }));
  const schoolsFetchError = useSelector((state) => fetchErrorSelector(state, { resource }));
  const isLoadingSchools = useSelector((state) => fetchingSelector(state, { resource }));
  const { data: schoolsData } = useSelector((state) => tableDataSelector(state, { resource }));
  const [searchParams, setSearchParams] = useSearchParams();
  const isAuthenticated = useSelector(authenticatedSelector);
  const [isFormOpened, setIsFormOpened] = useState(false);
  const [isSchoolSelectOpened, setIsSchoolSelectOpened] = useState(false);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [schoolMissing, setSchoolMissing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submitDone, setSubmitDone] = useState(false);
  const branchesData = _.flatten(
    schoolsData
      .filter((s) => (selectedSchools?.length ?? 0) === 0 || selectedSchools.includes(s.id))
      ?.map((s) => s.branches ?? [])
  );
  const [attachments, setAttachments] = useState([]);

  const user = useSelector(currentUserSelector);
  const { data, error, isFetching } = useSingleResource(params.offeringId, OFFERINGS_RESOURCE);

  useBreadcrumbs(OFFERING_DETAILS_BREADCRUMBS, isFetching, data);

  useEffect(() => {
    return () => {
      setAttachments([]);
      dispatch(resourceReset(FORMS_ENGINE_FORM_RESOURCE));
      dispatch(fetchReset(FORMS_ENGINE_FORM_RESOURCE));
    };
  }, [dispatch]);

  useEffect(() => {
    if (searchParams.get('open-form') === 'true' && isAuthenticated) {
      setSearchParams('');
      setIsSchoolSelectOpened(true);
    }
  }, [searchParams, isAuthenticated, setSearchParams]);

  useEffect(() => {
    if (isAuthenticated && shouldFetchSchools) {
      dispatch(fetchForTable(resource));
    }
  }, [dispatch, isAuthenticated, shouldFetchSchools]);

  useEffect(() => {
    if (schoolMissing && selectedBranches.length > 0 && selectedSchools.length > 0) {
      setSchoolMissing(false);
    }
  }, [selectedBranches, selectedSchools, schoolMissing]);

  const handleFormOpen = () => {
    setIsFormOpened(true);
  };

  const handleSchoolSelectOpen = () => {
    if (!isAuthenticated) {
      navigate(
        `${LOGIN_PATH}?fromUrl=${location.pathname}${location.search ? `${location.search}&` : '?'}open-form=true`
      );
    } else {
      setIsSchoolSelectOpened(true);
    }
  };

  const handleSchoolSelect = (schoolIds) => {
    setSelectedBranches(
      selectedBranches.filter((branchId) => {
        const branchSchoolId = branchesData.find((b) => b.id === branchId)?.schoolId;
        return schoolIds.includes(branchSchoolId);
      })
    );
    setSelectedSchools(schoolIds);
  };

  const handleSubmitStart = (_, onSuccess) => {
    setIsSubmitting(true);
    onSuccess();
  };

  const handleValidate = (_, onSuccess, onValidationError) => {
    if (selectedSchools.length === 0) {
      setSchoolMissing(true);
      return onValidationError();
    }
    setSchoolMissing(false);
    onSuccess();
  };

  const handleSubmitFormless = () => {
    setIsSubmitting(true);
    dossiersService.create(
      data?.dossierType,
      {
        data: JSON.stringify({
          offering: data,
          schools: schoolsData?.filter((s) => selectedSchools.includes(s.id)),
          branches: branchesData?.filter((b) => selectedBranches.includes(b.id)),
          submittedBy: getSubmittedByFromUser(user)
        }),
        attachments
      },
      {
        onSuccess: () => {
          setIsSubmitting(false);
          setSubmitDone(true);
        },
        onError: () => {
          setIsSubmitting(false);
          setSubmitError(true);
        }
      }
    );
  };

  const handleAttachmentsChange = (files) => {
    setAttachments(_.uniqBy([...attachments, ...Array.from(files)], (f) => f.name));
  };

  const handleAttachmentsDelete = (file) => {
    setAttachments(attachments?.filter((f) => f.name !== file.name));
  };

  const renderForm = () => {
    if (isSchoolSelectOpened) {
      return (
        <>
          {!isSubmitting && (
            <OfferingSchoolSelect
              isLoading={isLoadingSchools}
              hasError={!!schoolsFetchError}
              hasValidationError={schoolMissing}
              schools={schoolsData?.map((s) => ({ ...s, value: s.id, label: s.name }))}
              branches={branchesData}
              selectedSchools={selectedSchools}
              selectedBranches={selectedBranches}
              canGoToNextStep={selectedSchools.length > 0 && selectedBranches.length > 0}
              onBranchSelect={setSelectedBranches}
              onSchoolSelect={handleSchoolSelect}
              onNextClick={isFormOpened ? undefined : handleFormOpen}
            />
          )}
          {isFormOpened && (
            <>
              <FormsEngineWidget
                submitExtraData={{
                  offering: data,
                  schools: schoolsData?.filter((s) => selectedSchools.includes(s.id)),
                  branches: branchesData?.filter((b) => selectedBranches.includes(b.id)),
                  submittedBy: getSubmittedByFromUser(user),
                  [FORM_TYPE_PROP]: FORM_TYPES.OFFERING
                }}
                submitAttachments={attachments}
                onBeforeSubmit={handleSubmitStart}
                onValidate={handleValidate}
                slug={data?.formSlug}
                prefillConfig={{ firstName: 'firstName', lastName: 'lastName', email: 'email' }}
                prefillData={_.pick(user, 'firstName', 'lastName', 'email')}
              />
              {!isSubmitting && (
                <div className="u-padding">
                  <div className="a-input">
                    <label className="a-input__label">
                      Je kan hier optioneel bestanden toevoegen om je aanvraag verder te verduidelijken.
                    </label>
                    <FileUpload
                      multiple
                      files={attachments}
                      onChange={handleAttachmentsChange}
                      onDelete={handleAttachmentsDelete}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </>
      );
    }

    return (
      <Button className="u-margin-top u-margin-bottom" onClick={handleSchoolSelectOpen}>
        Aanvraagformulier invullen
      </Button>
    );
  };

  const renderFormless = () => {
    if (!data?.dossierType || data?.dossierType === DOSSIER_TYPES.NONE) {
      return null;
    }

    if (isSubmitting) {
      return (
        <>
          <h4 className="u-margin-bottom">{'Aangifte wordt verstuurd...'}</h4>
          <Spinner />
        </>
      );
    }

    if (submitDone) {
      return (
        <Alert type="success">
          <p>Jouw aanvraag is succesvol ingediend. We gaan hiermee aan de slag.</p>
          <p>
            De status van jouw aanvraag kan je steeds raadplegen in het overzicht{' '}
            <Link to={DOSSIERS_PATH}>Dossiers</Link>.
          </p>
        </Alert>
      );
    }

    if (submitError) {
      return <Alert type="danger">{'Aangifte indienen mislukt.'}</Alert>;
    }

    if (isSchoolSelectOpened) {
      return (
        <>
          <OfferingSchoolSelect
            isLoading={isLoadingSchools}
            hasError={!!schoolsFetchError}
            hasValidationError={schoolMissing}
            schools={schoolsData?.map((s) => ({ ...s, value: s.id, label: s.name }))}
            branches={branchesData}
            selectedSchools={selectedSchools}
            selectedBranches={selectedBranches}
            canGoToNextStep={selectedSchools.length > 0 && selectedBranches.length > 0}
            onBranchSelect={setSelectedBranches}
            onSchoolSelect={handleSchoolSelect}
            onNextClick={handleSubmitFormless}
            nextStepLabel="Indienen"
          />
          {!isSubmitting && (
            <div className="u-padding">
              <div className="a-input">
                <label className="a-input__label">
                  Je kan hier optioneel bestanden toevoegen om je aanvraag verder te verduidelijken.
                </label>
                <FileUpload
                  multiple
                  files={attachments}
                  onChange={handleAttachmentsChange}
                  onDelete={handleAttachmentsDelete}
                />
              </div>
            </div>
          )}
        </>
      );
    }

    return (
      <Button className="u-margin-top u-margin-bottom" onClick={handleSchoolSelectOpen}>
        Aanvraagformulier invullen
      </Button>
    );
  };

  return (
    <div className="spo-p-offering-details u-margin-top-md">
      <div className="spo-p-offering-details__header u-bg-light">
        <h2 className="u-padding">
          {isFetching ? (
            <div className="u-flex">
              <span className="u-margin-right">Aanbod ophalen</span>
              <Spinner />
            </div>
          ) : error ? (
            <div className="u-text-danger u-flex">
              <span className="u-margin-right">Opgelet</span>
              <Icon name="ai-alert-triangle" />
            </div>
          ) : (
            data?.title
          )}
        </h2>
      </div>
      <div className="spo-p-offering-details__content u-padding">
        <OfferingDetailView {...data}>
          {error ? (
            <p className="u-text-danger u-margin-top-xs">Er is iets mis gegaan, probeer het alstublieft nogmaals</p>
          ) : (
            <div
              className={`spo-p-offering-details__content-form u-margin-top-xs ${
                isSchoolSelectOpened ? 'school-select' : ''
              } ${isFormOpened ? 'opened' : ''} ${
                isSubmitting ? 'spo-p-offering-details__content-form--loading' : ''
              } ${submitError ? 'spo-p-offering-details__content-form--error' : ''}`}
            >
              {data?.formSlug && data?.dossierType !== DOSSIER_TYPES.NONE ? renderForm() : renderFormless()}
            </div>
          )}
        </OfferingDetailView>
      </div>
    </div>
  );
};

export default OfferingDetails;
